import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
// Aliasés : importés sous leur nom propre, ils masqueraient les types DOM
// globaux `KeyboardEvent`/`MouseEvent` dont ce fichier se sert par ailleurs.
import type { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent } from 'react';
import { D_ENTER_TIP, D_EXIT_TIP, usePrefersReducedMotion } from './motion';
import { IconAlert, IconAttachment, IconRefresh } from './icons';

export type Tone = 'accent' | 'green' | 'orange' | 'red' | 'purple' | 'teal' | 'pink' | 'indigo' | 'gray';

/** Couleur pleine et couleur douce associées à un ton sémantique. */
export function toneVars(tone: Tone): { color: string; soft: string } {
  if (tone === 'accent') return { color: 'var(--accent)', soft: 'var(--accent-soft)' };
  if (tone === 'gray') return { color: 'var(--gray)', soft: 'var(--surface-3)' };
  return { color: `var(--${tone})`, soft: `var(--${tone}-soft)` };
}

export function cx(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(' ');
}

// ---------------------------------------------------------------------------
// Bouton
// ---------------------------------------------------------------------------

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'plain';
  size?: 'sm' | 'md' | 'lg';
  tone?: Tone;
  icon?: ReactNode;
  trailingIcon?: ReactNode;
  loading?: boolean;
  block?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    tone = 'accent',
    icon,
    trailingIcon,
    loading = false,
    block = false,
    children,
    className,
    disabled,
    // `style` est sorti de `rest` et fusionné en dernier. Laissé dans `rest`, il
    // était étalé après l'attribut `style` du bouton et le remplaçait en entier
    // — hauteur, fond, bordure comprises : un appelant qui voulait seulement une
    // marge obtenait un bouton sans aucune apparence.
    style,
    ...rest
  },
  ref,
) {
  const { color, soft } = toneVars(tone);
  const heights = { sm: 30, md: 36, lg: 44 };
  const paddings = { sm: '0 10px', md: '0 14px', lg: '0 20px' };
  const fonts = { sm: 'var(--t-footnote)', md: 'var(--t-subhead)', lg: 'var(--t-headline)' };

  const base: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--s-2)',
    height: heights[size],
    padding: paddings[size],
    font: fonts[size],
    fontWeight: variant === 'primary' ? 600 : 500,
    borderRadius: 'var(--r-md)',
    // Bordure en propriétés longues, jamais en raccourci : les variantes ne
    // redéfinissent que la couleur, et React avertit — à juste titre — quand un
    // même style mélange `border` et `borderColor` d'un rendu à l'autre.
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'transparent',
    transition: 'background var(--d-fast) var(--ease), transform var(--d-fast) var(--ease), box-shadow var(--d-fast) var(--ease), opacity var(--d-fast)',
    whiteSpace: 'nowrap',
    width: block ? '100%' : undefined,
    opacity: disabled || loading ? 0.5 : 1,
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
  };

  const variants: Record<string, CSSProperties> = {
    primary: { background: color, color: 'var(--accent-contrast)', boxShadow: 'var(--shadow-sm)' },
    secondary: { background: 'var(--surface-2)', color: 'var(--text)', borderColor: 'var(--border)' },
    ghost: { background: soft, color },
    danger: { background: 'var(--red)', color: 'var(--on-solid)', boxShadow: 'var(--shadow-sm)' },
    plain: { background: 'transparent', color },
  };

  return (
    <button
      ref={ref}
      className={cx('bala-btn', className)}
      disabled={disabled || loading}
      {...rest}
      style={{ ...base, ...variants[variant], ...style }}
    >
      {loading ? <Spinner size={size === 'lg' ? 18 : 14} /> : icon}
      {children}
      {trailingIcon}
    </button>
  );
});

export function Spinner({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) {
  return (
    <span
      aria-hidden
      // Classe nommée : c'est par elle que « réduire les animations » ralentit
      // la rotation au lieu de la figer (cf. `tokens.css`).
      className="bala-spinner"
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        border: `2px solid ${color}`,
        borderTopColor: 'transparent',
        borderRadius: '50%',
        animation: 'bala-spin 620ms linear infinite',
        flexShrink: 0,
      }}
    />
  );
}

/** Bouton icône carré, pour les barres d'outils. */
export const IconButton = forwardRef<HTMLButtonElement, ButtonProps & { label: string }>(
  // `style` sorti de `rest` et fusionné en dernier, pour la même raison que
  // dans `Button` : laissé dans `rest`, il effaçait toute l'apparence.
  function IconButton(
    {
      label,
      size = 'md',
      variant = 'plain',
      tone = 'accent',
      className,
      children,
      style,
      // Typé `ButtonProps` mais ne déstructurant que sept propriétés, le
      // composant laissait ces quatre-là filer dans `rest` et les étalait sur
      // le `<button>` du DOM : React avertissait, et `<IconButton loading />`
      // ne faisait silencieusement rien. `loading` est désormais honoré.
      loading = false,
      icon,
      trailingIcon,
      block,
      disabled,
      ...rest
    },
    ref,
  ) {
    const dims = { sm: 28, md: 34, lg: 40 };
    const { color, soft } = toneVars(tone);
    return (
      <button
        ref={ref}
        aria-label={label}
        title={label}
        disabled={disabled || loading}
        className={cx('bala-icon-btn', className)}
        {...rest}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: dims[size],
          height: dims[size],
          borderRadius: 'var(--r-sm)',
          color: variant === 'primary' ? 'var(--accent-contrast)' : color,
          background: variant === 'primary' ? color : variant === 'ghost' ? soft : 'transparent',
          transition: 'background var(--d-fast) var(--ease), transform var(--d-fast) var(--ease)',
          flexShrink: 0,
          opacity: disabled || loading ? 0.5 : 1,
          cursor: disabled || loading ? 'not-allowed' : 'pointer',
          ...style,
        }}
      >
        {loading ? <Spinner size={size === 'lg' ? 18 : 14} /> : (icon ?? children)}
        {trailingIcon}
      </button>
    );
  },
);

// ---------------------------------------------------------------------------
// Surfaces
// ---------------------------------------------------------------------------

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  padding?: 'none' | 'sm' | 'md' | 'lg';
  interactive?: boolean;
  elevation?: 'flat' | 'sm' | 'md' | 'lg';
}

export function Card({
  padding = 'md',
  interactive = false,
  elevation = 'sm',
  className,
  style,
  children,
  onClick,
  onKeyDown,
  ...rest
}: CardProps) {
  const pads = { none: 0, sm: 'var(--s-3)', md: 'var(--s-4)', lg: 'var(--s-6)' };
  const shadows = { flat: 'none', sm: 'var(--shadow-sm)', md: 'var(--shadow-md)', lg: 'var(--shadow-lg)' };

  /*
   * Une carte porteuse d'un `onClick` est un bouton, quoi qu'en dise sa balise.
   * Elle était un `<div>` nu : la souris l'ouvrait, le clavier ne la voyait
   * même pas (WCAG 2.1.1, niveau A). Les trois attributs sont posés ici plutôt
   * qu'aux quinze points d'appel, pour qu'aucun nouveau ne puisse les oublier.
   *
   * `onClick` seul suffit à déclencher la bascule : `interactive` ne décrit que
   * l'apparence (le relief au survol), et plusieurs cartes cliquables ne le
   * passaient pas.
   */
  const clickable = Boolean(onClick);

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event);
    if (!clickable || event.defaultPrevented) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    // Un clic dans un champ ou un bouton imbriqué appartient à cet élément-là.
    if (event.target !== event.currentTarget) return;
    // Espace fait défiler la page par défaut ; Entrée valide un formulaire.
    event.preventDefault();
    onClick?.(event as unknown as ReactMouseEvent<HTMLDivElement>);
  };

  return (
    <div
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      className={cx('bala-card', interactive && 'bala-card-interactive', className)}
      style={{
        background: 'var(--surface)',
        // Propriétés longues : les appelants passent parfois `borderColor` seul
        // dans `style` pour signaler un état, et le raccourci entrerait en
        // conflit avec lui au rendu suivant.
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: 'var(--border)',
        borderRadius: 'var(--r-lg)',
        boxShadow: shadows[elevation],
        padding: pads[padding],
        transition: 'box-shadow var(--d-base) var(--ease), transform var(--d-base) var(--ease), border-color var(--d-base)',
        ...style,
      }}
      {...rest}
    >
      {children}
    </div>
  );
}

export function SectionHeader({
  title,
  subtitle,
  actions,
  level = 2,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  level?: 1 | 2 | 3;
}) {
  const fonts = { 1: 'var(--t-large-title)', 2: 'var(--t-title-2)', 3: 'var(--t-title-3)' };
  const Tag = (level === 1 ? 'h1' : level === 2 ? 'h2' : 'h3') as 'h1';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: 'var(--s-4)',
        flexWrap: 'wrap',
        marginBottom: 'var(--s-4)',
      }}
    >
      <div style={{ minWidth: 0 }}>
        {/* Les deux premiers niveaux sont en sérif : le crénage serré qui
            convient à une grotesque y colle les lettres. */}
        <Tag style={{ font: fonts[level], letterSpacing: level === 3 ? '-0.02em' : '-0.01em' }}>{title}</Tag>
        {subtitle && (
          <p style={{ font: 'var(--t-subhead)', color: 'var(--text-secondary)', marginTop: 'var(--s-1)' }}>
            {subtitle}
          </p>
        )}
      </div>
      {/* Les actions passent à la ligne plutôt que de déborder. Le conteneur
          extérieur enveloppait déjà, mais cette rangée-ci ne le faisait pas :
          au calendrier, quatre boutons sortaient de l'écran à 390 px et le
          dernier — « Nouvelle » — était coupé net. */}
      {actions && (
        <div style={{ display: 'flex', gap: 'var(--s-2)', alignItems: 'center', flexWrap: 'wrap' }}>
          {actions}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Badges & pastilles
// ---------------------------------------------------------------------------

export function Badge({
  children,
  tone = 'gray',
  variant = 'soft',
  size = 'md',
}: {
  children: ReactNode;
  tone?: Tone;
  variant?: 'soft' | 'solid' | 'outline';
  size?: 'sm' | 'md';
}) {
  const { color, soft } = toneVars(tone);
  const styles: Record<string, CSSProperties> = {
    soft: { background: soft, color },
    solid: { background: color, color: 'var(--on-solid)' },
    outline: { border: `1px solid ${color}`, color },
  };
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: size === 'sm' ? '1px 6px' : '3px 9px',
        borderRadius: 'var(--r-full)',
        font: size === 'sm' ? 'var(--t-caption-2)' : 'var(--t-caption)',
        fontWeight: 600,
        whiteSpace: 'nowrap',
        ...styles[variant],
      }}
    >
      {children}
    </span>
  );
}

export function Dot({ tone = 'gray', size = 8 }: { tone?: Tone; size?: number }) {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        borderRadius: '50%',
        background: toneVars(tone).color,
        flexShrink: 0,
      }}
    />
  );
}

export function Avatar({
  name,
  src,
  size = 36,
  tone,
}: {
  name: string;
  src?: string | null;
  size?: number;
  tone?: Tone;
}) {
  const letters = name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase();

  // Couleur dérivée du nom : stable d'un rendu à l'autre, sans stockage.
  const palette: Tone[] = ['accent', 'green', 'orange', 'purple', 'teal', 'pink', 'indigo'];
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash = (hash * 31 + name.charCodeAt(i)) % 997;
  const resolved = tone ?? palette[hash % palette.length]!;
  const { soft } = toneVars(resolved);

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        borderRadius: '50%',
        background: src ? 'var(--surface-3)' : soft,
        /*
         * Initiales à l'encre, pas dans la teinte de la pastille.
         *
         * Les deux partageaient la même couleur : sur un fond teinté à 14 %,
         * le vert tombait à 3,94:1 et l'orange à 3,62 — sous le seuil AA. La
         * teinte vient d'un hachage du nom, donc environ deux personnes sur
         * sept avaient des initiales illisibles, sans que rien ne le signale.
         * L'encre mesure 11,8 à 12,3 en clair et 10,1 à 10,4 en sombre, et la
         * pastille garde toute son identité colorée.
         */
        color: 'var(--text)',
        font: `600 ${Math.round(size * 0.38)}px/1 var(--font)`,
        overflow: 'hidden',
        flexShrink: 0,
        userSelect: 'none',
      }}
      title={name}
    >
      {src ? (
        <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        letters || '?'
      )}
    </span>
  );
}

/** Groupe d'avatars empilés, avec compteur de débordement. */
export function AvatarGroup({
  people,
  max = 4,
  size = 28,
}: {
  people: Array<{ name: string; src?: string | null }>;
  max?: number;
  size?: number;
}) {
  const shown = people.slice(0, max);
  const extra = people.length - shown.length;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      {shown.map((person, i) => (
        <span key={`${person.name}-${i}`} style={{ marginLeft: i === 0 ? 0 : -size * 0.3, position: 'relative', zIndex: shown.length - i }}>
          <span style={{ display: 'block', border: '2px solid var(--surface)', borderRadius: '50%' }}>
            <Avatar name={person.name} src={person.src} size={size} />
          </span>
        </span>
      ))}
      {extra > 0 && (
        <span
          style={{
            marginLeft: -size * 0.3,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: size,
            height: size,
            borderRadius: '50%',
            border: '2px solid var(--surface)',
            background: 'var(--surface-3)',
            color: 'var(--text-secondary)',
            font: `600 ${Math.round(size * 0.34)}px/1 var(--font)`,
          }}
        >
          +{extra}
        </span>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Champs de formulaire
// ---------------------------------------------------------------------------

const fieldStyle: CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  font: 'var(--t-subhead)',
  color: 'var(--text)',
  background: 'var(--surface-2)',
  // Même raison que pour les boutons : les champs invalides ne remplacent que
  // `borderColor`, le raccourci `border` créerait un conflit au rendu suivant.
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: 'var(--border)',
  borderRadius: 'var(--r-md)',
  transition: 'border-color var(--d-fast), box-shadow var(--d-fast), background var(--d-fast)',
};

/**
 * Identifiants qu'un `Field` transmet au champ qu'il contient.
 *
 * Sans cela, l'étiquette et le champ étaient frères sans lien : le `for` du
 * label ne désignait rien, et un lecteur d'écran annonçait « zone de texte »
 * sans dire laquelle. Le contexte évite d'imposer un `htmlFor` à la main sur
 * chaque appel — il y en a des dizaines, et l'oubli était silencieux.
 */
interface FieldBinding {
  id: string;
  describedBy?: string;
  /** Vrai quand le `Field` porte un message d'erreur. */
  invalid?: boolean;
}

const FieldContext = createContext<FieldBinding | null>(null);

/** Le champ hérite de l'identifiant du `Field`, sauf s'il en porte un à lui. */
function useFieldBinding(own: {
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean | 'true' | 'false' | 'grammar' | 'spelling';
}): {
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean | 'true' | 'false' | 'grammar' | 'spelling';
} {
  const binding = useContext(FieldContext);
  if (!binding) return {};
  return {
    id: own.id ?? binding.id,
    'aria-describedby': own['aria-describedby'] ?? binding.describedBy,
    // Le `Field` affichait la note rouge sans jamais le dire au champ : à
    // l'écran une bordure neutre sous un message d'erreur, au lecteur d'écran
    // rien du tout. L'appelant garde le dernier mot s'il pose l'attribut.
    'aria-invalid': own['aria-invalid'] ?? (binding.invalid || undefined),
  };
}

export function Field({
  label,
  hint,
  error,
  required,
  children,
  htmlFor,
}: {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  children: ReactNode;
  htmlFor?: string;
}) {
  const generated = useId();
  const id = htmlFor ?? generated;
  const noteId = `${id}-note`;
  const note = error ?? hint;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-2)', minWidth: 0 }}>
      {label && (
        <label
          htmlFor={id}
          style={{ font: 'var(--t-footnote)', fontWeight: 600, color: 'var(--text-secondary)' }}
        >
          {label}
          {required && <span style={{ color: 'var(--red)' }}> *</span>}
        </label>
      )}
      <FieldContext.Provider value={{ id, describedBy: note ? noteId : undefined, invalid: Boolean(error) }}>
        {children}
      </FieldContext.Provider>
      {error ? (
        <span id={noteId} style={{ font: 'var(--t-caption)', color: 'var(--red)' }}>
          {error}
        </span>
      ) : hint ? (
        <span id={noteId} style={{ font: 'var(--t-caption)', color: 'var(--text-tertiary)' }}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  invalid?: boolean;
  /** Contenu affiché avant le champ (icône, unité). */
  leading?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { invalid, leading, style, ...rest },
  ref,
) {
  const binding = useFieldBinding(rest);
  if (leading) {
    // L'anneau se dessine sur l'enveloppe : c'est elle qu'on voit, l'input
    // interne n'ayant ni bordure ni fond. Sans ça, `outline: none` plus bas
    // rendait tout champ à icône invisible au clavier.
    return (
      <div
        className="bala-input-wrap"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--s-2)',
          ...fieldStyle,
          padding: '0 12px',
          borderColor: invalid ? 'var(--red)' : 'var(--border)',
        }}
      >
        {/* `flexShrink: 0` : sans ça, le champ voisin comprimait l'indicatif
            téléphonique jusqu'à le rendre illisible dans une colonne étroite. */}
        <span style={{ color: 'var(--text-tertiary)', display: 'flex', flexShrink: 0 }}>{leading}</span>
        <input
          ref={ref}
          style={{
            flex: 1,
            minWidth: 0,
            border: 'none',
            background: 'none',
            outline: 'none',
            padding: '9px 0',
            font: 'var(--t-subhead)',
            ...style,
          }}
          {...binding}
          {...rest}
        />
      </div>
    );
  }
  return (
    <input
      ref={ref}
      style={{ ...fieldStyle, borderColor: invalid ? 'var(--red)' : 'var(--border)', ...style }}
      {...binding}
      {...rest}
    />
  );
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }>(
  function Textarea({ invalid, style, rows = 4, ...rest }, ref) {
    const binding = useFieldBinding(rest);
    return (
      <textarea
        ref={ref}
        rows={rows}
        style={{
          ...fieldStyle,
          borderColor: invalid ? 'var(--red)' : 'var(--border)',
          resize: 'vertical',
          lineHeight: 1.5,
          ...style,
        }}
        {...binding}
        {...rest}
      />
    );
  },
);

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ style, children, ...rest }, ref) {
    const binding = useFieldBinding(rest);
    return (
      <select
        ref={ref}
        style={{
          ...fieldStyle,
          appearance: 'none',
          paddingRight: 32,
          cursor: 'pointer',
          // Chevron dessiné en SVG inline pour éviter une requête réseau.
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='none' stroke='%238e8e93' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round' d='M1 1.5 6 6.5l5-5'/%3E%3C/svg%3E\")",
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 12px center',
          ...style,
        }}
        {...binding}
        {...rest}
      >
        {children}
      </select>
    );
  },
);

/**
 * Interrupteur compact (track + pouce superposés).
 *
 * La zone tactile reste à 44×44 px ; le rendu visuel est plus petit pour ne
 * pas écraser les libellés des listes de préférences. Le pouce est animé en
 * `transform` (via `--switch-x`) pour un rendu net, sans effet « croissant ».
 */
export function Switch({
  checked,
  onChange,
  disabled,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  label?: string;
}) {
  // Course du pouce : piste 48 − pouce 22 − marges 2×3 = 20 px.
  const travel = 20;

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="bala-switch"
      style={{ ['--switch-x' as string]: checked ? `${travel}px` : '0px' } as CSSProperties}
    >
      <span aria-hidden className="bala-switch__track">
        <span className="bala-switch__thumb" />
      </span>
    </button>
  );
}

export function Checkbox({
  checked,
  onChange,
  label,
  disabled,
  indeterminate = false,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label?: ReactNode;
  disabled?: boolean;
  indeterminate?: boolean;
}) {
  const id = useId();
  return (
    <label
      htmlFor={id}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--s-2)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        font: 'var(--t-subhead)',
      }}
    >
      <input
        id={id}
        type="checkbox"
        className="bala-checkbox__input"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
      />
      <span
        aria-hidden
        className="bala-checkbox__box"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 20,
          height: 20,
          borderRadius: 'var(--r-xs)',
          border: `1.5px solid ${checked || indeterminate ? 'var(--accent)' : 'var(--border-strong)'}`,
          background: checked || indeterminate ? 'var(--accent)' : 'transparent',
          color: 'var(--accent-contrast)',
          transition: 'background var(--d-fast) var(--ease), border-color var(--d-fast)',
          flexShrink: 0,
        }}
      >
        {indeterminate ? (
          <svg width="10" height="2" viewBox="0 0 10 2"><rect width="10" height="2" rx="1" fill="currentColor" /></svg>
        ) : checked ? (
          <svg width="13" height="10" viewBox="0 0 13 10">
            <path d="M1 5l3.5 3.5L12 1" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : null}
      </span>
      {label}
    </label>
  );
}

/** Groupe de segments façon iOS, pour les sous-onglets et les filtres. */
export function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  size = 'md',
  block = false,
}: {
  value: T;
  options: Array<{ value: T; label: ReactNode; badge?: number }>;
  onChange: (next: T) => void;
  size?: 'sm' | 'md';
  block?: boolean;
}) {
  /*
   * Le groupe glisse plutôt que d'être coupé.
   *
   * Il était en `inline-flex` avec `white-space: nowrap` et aucun débordement :
   * aux Finances, le quatrième onglet « Catégories » sortait de l'écran à
   * 390 px sans qu'aucun indice ne le laisse deviner. Le contenu défile
   * désormais au doigt, et un dégradé signale le côté où il reste des onglets.
   */
  const railRef = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState<'none' | 'start' | 'end' | 'both'>('none');

  const measure = useCallback(() => {
    const rail = railRef.current;
    if (!rail) return;
    // 2 px de tolérance : les navigateurs arrondissent `scrollWidth`, et sans
    // marge le dégradé clignotait en fin de course.
    const debutCache = rail.scrollLeft > 2;
    const finCachee = rail.scrollLeft + rail.clientWidth < rail.scrollWidth - 2;
    setOverflow(
      debutCache && finCachee ? 'both' : finCachee ? 'end' : debutCache ? 'start' : 'none',
    );
  }, []);

  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    measure();
    rail.addEventListener('scroll', measure, { passive: true });
    // Le débordement dépend de la largeur disponible, qui change à la rotation
    // de l'appareil comme à l'ouverture de la barre latérale.
    const observer = new ResizeObserver(measure);
    observer.observe(rail);
    return () => {
      rail.removeEventListener('scroll', measure);
      observer.disconnect();
    };
  }, [measure, options.length]);

  // L'onglet actif doit être visible : sur un lien profond, il pouvait se
  // trouver hors de la partie visible du rail.
  useEffect(() => {
    const actif = railRef.current?.querySelector<HTMLElement>('[aria-selected="true"]');
    actif?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
  }, [value]);

  return (
    <div
      className="bala-rail-wrap"
      data-overflow={overflow === 'none' ? undefined : overflow}
      style={{ display: block ? 'block' : 'inline-block' }}
    >
    <div
      role="tablist"
      ref={railRef}
      className="bala-rail"
      style={{
        gap: 2,
        padding: 2,
        background: 'var(--surface-3)',
        borderRadius: 'var(--r-md)',
        width: block ? '100%' : undefined,
      }}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(option.value)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              flex: block ? 1 : undefined,
              padding: size === 'sm' ? '4px 10px' : '6px 14px',
              font: size === 'sm' ? 'var(--t-footnote)' : 'var(--t-subhead)',
              fontWeight: active ? 600 : 500,
              color: active ? 'var(--text)' : 'var(--text-secondary)',
              background: active ? 'var(--surface)' : 'transparent',
              borderRadius: 'var(--r-sm)',
              boxShadow: active ? 'var(--shadow-xs)' : 'none',
              transition: 'background var(--d-fast) var(--ease), color var(--d-fast), box-shadow var(--d-fast)',
              whiteSpace: 'nowrap',
            }}
          >
            {option.label}
            {option.badge != null && option.badge > 0 && (
              <span
                style={{
                  font: 'var(--t-caption-2)',
                  padding: '0 5px',
                  borderRadius: 'var(--r-full)',
                  background: active ? 'var(--accent-soft)' : 'var(--surface-3)',
                  color: active ? 'var(--accent)' : 'var(--text-secondary)',
                }}
              >
                {option.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// États vides & chargement
// ---------------------------------------------------------------------------

export function EmptyState({
  icon,
  title,
  description,
  action,
  compact = false,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
  compact?: boolean;
}) {
  return (
    <div
      className="bala-appear"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: 'var(--s-3)',
        padding: compact ? 'var(--s-6) var(--s-4)' : 'var(--s-12) var(--s-6)',
        color: 'var(--text-secondary)',
      }}
    >
      {icon && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: compact ? 44 : 60,
            height: compact ? 44 : 60,
            borderRadius: 'var(--r-xl)',
            background: 'var(--surface-3)',
            color: 'var(--text-tertiary)',
            fontSize: compact ? 22 : 28,
          }}
        >
          {icon}
        </div>
      )}
      <div>
        <p style={{ font: compact ? 'var(--t-headline)' : 'var(--t-title-3)', color: 'var(--text)' }}>{title}</p>
        {description && (
          <p style={{ font: 'var(--t-subhead)', marginTop: 'var(--s-1)', maxWidth: 380 }}>{description}</p>
        )}
      </div>
      {action}
    </div>
  );
}

/**
 * Pendant d'`EmptyState` pour ce qui n'a pas pu être chargé.
 *
 * Les deux se ressemblent volontairement — même composition, même densité —
 * mais ils ne disent pas la même chose : `EmptyState` annonce qu'il n'y a rien,
 * celui-ci qu'on ne sait pas. Les confondre revient à affirmer « aucune
 * activité » quand le serveur est simplement injoignable, ce qui est un
 * mensonge que l'utilisateur n'a aucun moyen de détecter.
 *
 * Le message technique reste consultable mais n'est pas le titre : « Failed to
 * fetch » n'aide personne, « Réessayer » si.
 */
export function ErrorState({
  title = 'Chargement impossible',
  description,
  detail,
  onRetry,
  compact = false,
}: {
  title?: string;
  description?: ReactNode;
  /** Message brut de l'API, affiché en retrait pour le diagnostic. */
  detail?: string | null;
  onRetry?: () => void;
  compact?: boolean;
}) {
  return (
    <div
      role="alert"
      className="bala-appear"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: 'var(--s-3)',
        padding: compact ? 'var(--s-6) var(--s-4)' : 'var(--s-12) var(--s-6)',
        color: 'var(--text-secondary)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: compact ? 44 : 60,
          height: compact ? 44 : 60,
          borderRadius: 'var(--r-xl)',
          background: 'var(--orange-soft)',
          color: 'var(--orange)',
        }}
      >
        <IconAlert size={compact ? 20 : 26} />
      </div>
      <div>
        <p style={{ font: compact ? 'var(--t-headline)' : 'var(--t-title-3)', color: 'var(--text)' }}>{title}</p>
        <p style={{ font: 'var(--t-subhead)', marginTop: 'var(--s-1)', maxWidth: 380 }}>
          {description ?? 'Le serveur n’a pas répondu. Vos données ne sont pas perdues.'}
        </p>
        {detail && (
          <p style={{ font: 'var(--t-caption)', marginTop: 'var(--s-2)', color: 'var(--text-tertiary)' }}>{detail}</p>
        )}
      </div>
      {onRetry && (
        <Button variant="secondary" size="sm" icon={<IconRefresh size={14} />} onClick={onRetry}>
          Réessayer
        </Button>
      )}
    </div>
  );
}

/** Bloc gris animé pendant le chargement. */
export function Skeleton({
  width = '100%',
  height = 16,
  radius = 'var(--r-sm)',
}: {
  width?: number | string;
  height?: number | string;
  radius?: string;
}) {
  return (
    <span
      aria-hidden
      className="bala-skeleton"
      style={{
        display: 'block',
        width,
        height,
        borderRadius: radius,
        background:
          'linear-gradient(90deg, var(--surface-2) 25%, var(--surface-3) 50%, var(--surface-2) 75%)',
        backgroundSize: '200% 100%',
        animation: 'bala-shimmer 1.4s linear infinite',
      }}
    />
  );
}

export function SkeletonCard({ lines = 3 }: { lines?: number }) {
  return (
    <Card>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-3)' }}>
        <Skeleton width="45%" height={20} />
        {Array.from({ length: lines }, (_, i) => (
          <Skeleton key={i} width={i === lines - 1 ? '65%' : '100%'} height={12} />
        ))}
      </div>
    </Card>
  );
}

export function LoadingScreen({ message = 'Chargement…' }: { message?: string }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--s-4)',
        minHeight: '50dvh',
        color: 'var(--text-secondary)',
        font: 'var(--t-subhead)',
      }}
    >
      <Spinner size={24} color="var(--accent)" />
      {message}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Barre de progression
// ---------------------------------------------------------------------------

export function ProgressBar({
  value,
  max = 100,
  tone = 'accent',
  height = 8,
  showLabel = false,
}: {
  value: number;
  max?: number;
  tone?: Tone;
  height?: number;
  showLabel?: boolean;
}) {
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-2)', width: '100%' }}>
      <div
        role="progressbar"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
        style={{
          flex: 1,
          height,
          borderRadius: 'var(--r-full)',
          background: 'var(--surface-3)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            // Remplissage par `scaleX` et non par `width` : animer une largeur
            // force un calcul de mise en page à chaque image, alors qu'une
            // transformation reste sur le compositeur. Rendu identique.
            width: '100%',
            height: '100%',
            borderRadius: 'var(--r-full)',
            background: toneVars(tone).color,
            transformOrigin: 'left',
            transform: `scaleX(${pct / 100})`,
            transition: 'transform var(--d-slow) var(--ease-out)',
          }}
        />
      </div>
      {showLabel && (
        <span style={{ font: 'var(--t-caption)', color: 'var(--text-secondary)', minWidth: 36, textAlign: 'right' }}>
          {Math.round(pct)} %
        </span>
      )}
    </div>
  );
}

/** Anneau de progression, utilisé par l'indicateur « réunion prête ». */
export function ProgressRing({
  value,
  size = 56,
  thickness = 6,
  tone = 'accent',
  label,
  ariaLabel,
}: {
  value: number;
  size?: number;
  thickness?: number;
  tone?: Tone;
  label?: ReactNode;
  /** Ce que la proportion mesure, pour l'annonce vocale. */
  ariaLabel?: string;
}) {
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = Math.min(100, Math.max(0, value));
  return (
    /*
     * `ProgressBar` était correctement décrit, l'anneau ne l'était pas du tout :
     * même information, rendue muette parce qu'elle est dessinée en SVG. Le
     * libellé central est souvent un fragment (« 12/18 ») qui ne se suffit pas
     * hors contexte, d'où `aria-valuetext` en plus du pourcentage.
     */
    <div
      role="progressbar"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuetext={`${Math.round(pct)} %`}
      aria-label={ariaLabel}
      style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}
    >
      <svg aria-hidden width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="var(--surface-3)" strokeWidth={thickness} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={toneVars(tone).color}
          strokeWidth={thickness}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - pct / 100)}
          style={{ transition: 'stroke-dashoffset var(--d-slow) var(--ease-out)' }}
        />
      </svg>
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          font: 'var(--t-caption)',
          fontWeight: 600,
        }}
      >
        {label ?? `${Math.round(pct)}%`}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Divers
// ---------------------------------------------------------------------------

export function Divider({ vertical = false, spacing = 'var(--s-4)' }: { vertical?: boolean; spacing?: string }) {
  return (
    <div
      aria-hidden
      style={
        vertical
          ? { width: 1, alignSelf: 'stretch', background: 'var(--separator)', margin: `0 ${spacing}` }
          : { height: 1, background: 'var(--separator)', margin: `${spacing} 0` }
      }
    />
  );
}

/** Ligne clé/valeur des fiches détail. */
export function DetailRow({
  label,
  children,
  icon,
}: {
  label: ReactNode;
  children: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 'var(--s-3)',
        alignItems: 'baseline',
        padding: 'var(--s-2) 0',
        borderBottom: '1px solid var(--separator)',
      }}
    >
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          font: 'var(--t-footnote)',
          color: 'var(--text-secondary)',
          minWidth: 130,
          flexShrink: 0,
        }}
      >
        {icon}
        {label}
      </span>
      <span style={{ font: 'var(--t-subhead)', minWidth: 0, flex: 1 }}>{children}</span>
    </div>
  );
}

/** Infobulle au survol et au focus clavier. */
export function Tooltip({ content, children }: { content: ReactNode; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const tipId = useId();
  // Même découpage que le menu : `mounted` maintient le nœud le temps de la
  // sortie, `shown` porte l'état visuel une image plus tard.
  const [mounted, setMounted] = useState(false);
  const [shown, setShown] = useState(false);
  const reducedMotion = usePrefersReducedMotion();
  const tipDuration = reducedMotion ? 0 : open ? D_ENTER_TIP : D_EXIT_TIP;

  useEffect(() => {
    if (open) {
      setMounted(true);
      const frame = requestAnimationFrame(() => {
        requestAnimationFrame(() => setShown(true));
      });
      return () => cancelAnimationFrame(frame);
    }
    setShown(false);
    const timer = window.setTimeout(() => setMounted(false), tipDuration);
    return () => window.clearTimeout(timer);
  }, [open, tipDuration]);

  return (
    /*
     * `aria-describedby` sur l'enveloppe : le `role="tooltip"` existait mais
     * n'était référencé par rien, donc jamais annoncé. Échap ferme, comme le
     * demande WCAG 1.4.13 pour tout contenu apparu au survol.
     */
    <span
      aria-describedby={mounted ? tipId : undefined}
      style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      onKeyDown={(event) => {
        if (event.key === 'Escape') setOpen(false);
      }}
    >
      {children}
      {mounted && (
        /*
         * Deux éléments plutôt qu'un : le `transform` d'une animation remplace
         * celui de l'élément pendant toute sa durée. Portés par le même nœud,
         * le `translateX(-50%)` de centrage disparaissait les 140 ms de
         * `bala-pop` — l'infobulle s'ouvrait décalée d'une demi-largeur puis
         * glissait en place. L'enveloppe positionne, l'intérieur anime.
         * (Même découpage que le Toast, cf. `overlays.tsx`.)
         */
        <span
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 6px)',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 'var(--z-toast)',
            // Volontairement pas `pointerEvents: 'none'` : l'infobulle doit
            // rester survolable pour qu'on puisse en atteindre le texte sans
            // qu'elle s'évapore (WCAG 1.4.13). Le survol de l'enveloppe la
            // maintient ouverte, puisqu'elle en est un descendant.
          }}
        >
          <span
            role="tooltip"
            id={tipId}
            style={{
              display: 'block',
              padding: '5px 9px',
              borderRadius: 'var(--r-sm)',
              background: 'var(--text)',
              color: 'var(--bg-elevated)',
              font: 'var(--t-caption)',
              whiteSpace: 'nowrap',
              boxShadow: 'var(--shadow-md)',
              // L'infobulle se pose au-dessus de son ancre : elle grandit donc
              // depuis son bord bas, du côté de ce qu'elle désigne.
              transformOrigin: 'bottom center',
              opacity: shown ? 1 : 0,
              transform: shown ? 'none' : 'scale(0.94)',
              transition: `opacity ${tipDuration}ms var(--ease-out), transform ${tipDuration}ms var(--ease-out)`,
            }}
          >
            {content}
          </span>
        </span>
      )}
    </span>
  );
}

/**
 * Zone de dépôt de fichiers. Gère le glisser-déposer et le clic, et laisse
 * l'appelant décider quoi faire des fichiers.
 */
export function DropZone({
  onFiles,
  accept,
  multiple = true,
  children,
  compact = false,
}: {
  onFiles: (files: File[]) => void;
  accept?: string;
  multiple?: boolean;
  children?: ReactNode;
  compact?: boolean;
}) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div
      onDragOver={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        const files = [...event.dataTransfer.files];
        if (files.length > 0) onFiles(multiple ? files : files.slice(0, 1));
      }}
      onClick={() => inputRef.current?.click()}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          inputRef.current?.click();
        }
      }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--s-2)',
        padding: compact ? 'var(--s-4)' : 'var(--s-8)',
        border: `1.5px dashed ${dragging ? 'var(--accent)' : 'var(--border-strong)'}`,
        borderRadius: 'var(--r-lg)',
        background: dragging ? 'var(--accent-soft)' : 'var(--surface-2)',
        color: dragging ? 'var(--accent)' : 'var(--text-secondary)',
        font: 'var(--t-subhead)',
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'background var(--d-fast), border-color var(--d-fast), color var(--d-fast)',
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={(event) => {
          const files = [...(event.target.files ?? [])];
          if (files.length > 0) onFiles(files);
          event.target.value = '';
        }}
        style={{ display: 'none' }}
      />
      {children ?? (
        <>
          <IconAttachment size={24} />
          <span>Glissez des fichiers ici ou cliquez pour parcourir</span>
        </>
      )}
    </div>
  );
}

/** Barre de recherche avec effacement rapide. */
export function SearchInput({
  value,
  onChange,
  placeholder = 'Rechercher…',
  autoFocus = false,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  return (
    <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
      <span
        aria-hidden
        style={{
          position: 'absolute',
          left: 11,
          top: '50%',
          transform: 'translateY(-50%)',
          color: 'var(--text-tertiary)',
          display: 'flex',
          pointerEvents: 'none',
        }}
      >
        <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
          <circle cx="6.5" cy="6.5" r="4.75" stroke="currentColor" strokeWidth="1.6" />
          <path d="M10.2 10.2 13.5 13.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </span>
      <input
        type="search"
        value={value}
        autoFocus={autoFocus}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        style={{ ...fieldStyle, paddingLeft: 34, paddingRight: value ? 32 : 12 }}
      />
      {value && (
        <button
          type="button"
          aria-label="Effacer la recherche"
          onClick={() => onChange('')}
          style={{
            position: 'absolute',
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 18,
            height: 18,
            borderRadius: '50%',
            background: 'var(--text-tertiary)',
            color: 'var(--surface)',
            font: 'var(--t-caption-2)',
          }}
        >
          ✕
        </button>
      )}
    </div>
  );
}

/** Piège le focus dans un conteneur (modales, panneaux). */
export function useFocusTrap(active: boolean) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!active || !ref.current) return;
    const container = ref.current;
    const previous = document.activeElement as HTMLElement | null;

    const focusables = () =>
      [
        ...container.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ].filter((element) => element.offsetParent !== null);

    focusables()[0]?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', onKeyDown);
    return () => {
      container.removeEventListener('keydown', onKeyDown);
      previous?.focus();
    };
  }, [active]);

  return ref;
}
